import React from "react";
import { Link } from "react-router-dom";
import "./Admin.css";

const guideItems = [
  {
    title: "Draft",
    summary:
      "A working copy that students cannot see or attempt.",
    details:
      "Use Draft while creating or reviewing an exam. Draft exams can be edited freely and deleted if no student attempts exist.",
  },
  {
    title: "Published",
    summary:
      "An approved exam that can be made available to students.",
    details:
      "Published means the exam has passed admin review. Active controls whether students can currently see and attempt it.",
  },
  {
    title: "Archived",
    summary:
      "A retired exam kept for history and result integrity.",
    details:
      "Archived exams are hidden from students and inactive. Use Archive instead of Delete when an exam has attempts or should remain as an audit record.",
  },
  {
    title: "Active / Deactivate",
    summary:
      "Controls whether a published exam is currently available to students in the exam list.",
    details:
      "Use Activate when the exam is ready for students. Use Deactivate to temporarily hide a published exam without changing its content or deleting results.",
  },
  {
    title: "Edit",
    summary:
      "Updates exam details such as title, level, time limit, pass marks, technologies, and questions.",
    details:
      "Questions are locked after any student attempt exists, so results stay consistent. In that case, edit only the exam details or create a fresh exam manually.",
  },
  {
    title: "Publish / Unpublish",
    summary:
      "Moves an exam between draft mode and published mode.",
    details:
      "Publish makes the exam eligible to appear for students. Unpublish returns it to draft mode and also makes it inactive, which is useful when an exam needs review.",
  },
];

const quizJsonExample = `{
  "quizId": "javascript-basics-easy",
  "title": "JavaScript Basics - Easy Exam",
  "description": "Basic exam covering JavaScript variables, functions, arrays, and conditions.",
  "timeLimit": 30,
  "level": "Easy",
  "passMarks": 3,
  "technologies": ["JavaScript", "Web Development"],
  "status": "DRAFT",
  "questions": [
    {
      "questionText": "Which keyword is used to declare a constant in JavaScript?",
      "imageUrl": "",
      "imagePublicId": "",
      "imageAlt": "",
      "codeSnippet": "const x = 10;\\nconsole.log(x);",
      "codeLanguage": "javascript",
      "options": ["var", "let", "const", "static"],
      "correctAnswer": "const"
    },
    {
      "questionText": "Which method adds an item to the end of an array?",
      "imageUrl": "",
      "imagePublicId": "",
      "imageAlt": "",
      "codeSnippet": "",
      "codeLanguage": "",
      "options": ["push()", "pop()", "shift()", "unshift()"],
      "correctAnswer": "push()"
    },
    {
      "questionText": "What is the result of typeof [] in JavaScript?",
      "imageUrl": "",
      "imagePublicId": "",
      "imageAlt": "",
      "codeSnippet": "",
      "codeLanguage": "",
      "options": ["array", "object", "list", "undefined"],
      "correctAnswer": "object"
    },
    {
      "questionText": "Which operator is used for strict equality?",
      "imageUrl": "",
      "imagePublicId": "",
      "imageAlt": "",
      "codeSnippet": "",
      "codeLanguage": "",
      "options": ["==", "===", "!=", "="],
      "correctAnswer": "==="
    },
    {
      "questionText": "Which function prints output to the browser console?",
      "imageUrl": "",
      "imagePublicId": "",
      "imageAlt": "",
      "codeSnippet": "",
      "codeLanguage": "",
      "options": ["print()", "console.log()", "echo()", "write()"],
      "correctAnswer": "console.log()"
    }
  ]
}`;

const AdminExamGuide = () => {
  return (
    <section className="admin-page">
      <div className="admin-split-head">
        <div>
          <h2>Exam Management Guide</h2>
          <p>Use this page as a quick reference before changing exam availability or content.</p>
        </div>
        <div className="actions-row">
          <Link className="admin-link-button" to="/admin/quizzes">Create Exam</Link>
          <Link className="admin-link-button secondary" to="/admin/manage-exams">Manage Exams</Link>
        </div>
      </div>

      <div className="admin-guide-grid">
        {guideItems.map((item) => (
          <article className="admin-card guide-card" key={item.title}>
            <h3>{item.title}</h3>
            <p className="guide-summary">{item.summary}</p>
            <p>{item.details}</p>
          </article>
        ))}
      </div>

      <div className="admin-card guide-flow">
        <h3>Recommended Admin Flow</h3>
        <ol>
          <li>Create the exam as Draft.</li>
          <li>Review questions, answers, pass marks, and time limit.</li>
          <li>Publish the exam when it is approved.</li>
          <li>Activate the exam when students should be able to take it.</li>
          <li>Deactivate it for a temporary pause, Unpublish it for review, or Archive it when retired.</li>
        </ol>
      </div>

      <div className="admin-card guide-json-section">
        <h3>Create Exams Using JSON</h3>
        <p>
          Paste this format into Create Exam, then click Validate JSON and Confirm Import.
        </p>
        <pre className="guide-json-example">
          <code>{quizJsonExample}</code>
        </pre>
        <div className="guide-json-rules">
          <strong>JSON rules</strong>
          <ul>
            <li>quizId must be unique.</li>
            <li>level must be Easy, Medium, or Hard.</li>
            <li>status should usually be DRAFT, then publish from Manage Exams.</li>
            <li>passMarks cannot be greater than the number of questions.</li>
            <li>correctAnswer must exactly match one option in the same question.</li>
            <li>For image questions, leave image fields empty in JSON, import it, then upload the image on that question card.</li>
            <li>For programming questions, add codeSnippet and set codeLanguage such as python, javascript, java, c, cpp, html, css, sql, or json.</li>
          </ul>
        </div>
      </div>
    </section>
  );
};

export default AdminExamGuide;
